import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { get } from 'lodash';

import { FormRow } from 'components/Form';
import { Button } from 'components/Button';
import GeneralError from 'components/GeneralError';

import useWorkflowGuard from 'hooks/useWorkflowGuard';
import { nextAction } from 'actions/workflow';

const KBAIdentityQuestions = ({
  navigate,
  searchParams,
  dispatch,
}) => {
  useWorkflowGuard(searchParams, navigate);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [generalError, setGeneralError] = useState(null);
  const [answers, setAnswers] = useState({});
  const checkinData = useSelector((state) => get(state, 'workflow.data'));
  const questions = get(checkinData, 'questions') || [];
  const questionSetId = get(checkinData, 'questionSetId');

  useEffect(() => {
    setAnswers({});
    setGeneralError(null);
  }, [questionSetId]);

  const handleAnswerChange = (questionId, choiceId) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: choiceId
    }));
  };

  const handleFail = (err) => {
    setGeneralError(get(err, 'data.failure.errors.0'));
    setAnswers({});
    setIsSubmitting(false);
  }

  const handleSubmit = () => {
    setGeneralError(null);

    if (Object.keys(answers).length !== questions.length) {
      setGeneralError({ message: 'Please answer all of the questions provided.' });
      return;
    }

    const answersArray = Object.entries(answers).map(([questionId, choiceId]) => ({
      questionId,
      choiceId
    }));

    setIsSubmitting(true);
    dispatch(nextAction({
      key: searchParams.get('key'),
      data: {
        answers: answersArray,
        conversationId: get(checkinData, 'conversationId'),
        questionSetId: get(checkinData, 'questionSetId'),
      },
      success: (res) => {
        navigate(get(res, 'data.url'));
        setIsSubmitting(false);
      },
      fail: handleFail,
    }))
  };

  return (
    <div className="flex flex-col items-center">
      <h2 className="text-base font-semibold text-slate-700 text-center mb-2">
        ID Check - Identification Questions
      </h2>
      <p className="text-sm text-slate-500 text-center mb-8 max-w-xl">
        Please respond to the questions below so that we may verify your identity. Once your identity has been verified, you'll be able to sign your loan documents.
      </p>

      <div className="w-full space-y-6 mb-6">
        {questions.map((question) => (
          <div key={question.questionId}>
            <h3 className="text-sm font-semibold text-slate-900 mb-3">
              {question.question}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {question.choices.map((choice) => (
                <label key={choice.choiceId} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name={question.questionId}
                    className="w-4 h-4 text-blue-500"
                    checked={answers[question.questionId] === choice.choiceId}
                    onChange={() => handleAnswerChange(question.questionId, choice.choiceId)}
                  />
                  <span className="text-sm text-slate-700">{choice.choice}</span>
                </label>
              ))}
            </div>
          </div>
        ))}
      </div>

      <GeneralError error={generalError} className="w-full mb-4" />

      <FormRow className="w-full flex-col-reverse">
        <Button
          className="flex-1"
          label="Submit"
          onClick={handleSubmit}
          isLoading={isSubmitting}
        />
      </FormRow>
    </div>
  );
};

KBAIdentityQuestions.propTypes = {
  navigate: PropTypes.func.isRequired,
  searchParams: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired,
};

export default KBAIdentityQuestions;
